import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { validateContextEligibility, updateEvaluatorContext } from '../services/contextService';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getAutomationById } from '@/services/automation';
import { db, collections } from '@/lib/firebase';
import Header from '../components/Header';
import { ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { Navigate } from 'react-router-dom';

export default function ProvideContext() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [evaluatorData, setEvaluatorData] = useState<any>(null);
  const [userData, setUserData] = useState({ firstName: '', lastName: '' });
  const [formUrl, setFormUrl] = useState<string>('');

  useEffect(() => {
    async function validateAccess() {
      if (!currentUser) return;

      try {
        const params = new URLSearchParams(location.search);
        const evaluatorId = params.get('evaluatorId');

        if (!evaluatorId) {
          throw new Error('Invalid access: Missing evaluator ID');
        }

        const [{ eligible, message }, automation] = await Promise.all([
          validateContextEligibility(evaluatorId, currentUser.uid),
          getAutomationById('context-form')
        ]);

        if (!eligible) {
          setError(message);
          setTimeout(() => navigate('/dashboard'), 3000);
          return;
        }

        // Get evaluator data
        const evaluatorsRef = collection(db, collections.evaluators);
        const q = query(evaluatorsRef, where('id', '==', evaluatorId));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          throw new Error('Evaluator not found');
        }

        const evaluatorDoc = snapshot.docs[0];
        const evaluatorData = evaluatorDoc.data();
        setEvaluatorData(evaluatorData);

        if (!automation?.url) {
          throw new Error('Context form configuration not found');
        }

        const url = new URL(automation.url);
        // Add parameters to hash instead of search params
        const hashParams = new URLSearchParams();
        hashParams.set('userid', evaluatorData.userId);
        hashParams.set('evaluatorfirstname', evaluatorData.firstName);
        hashParams.set('evaluatorlastname', evaluatorData.lastName);
        hashParams.set('evaluatoremail', evaluatorData.email);
        hashParams.set('evaluatorid', evaluatorId);
        hashParams.set('useremail', params.get('userEmail') || '');
        url.hash = hashParams.toString();
        setFormUrl(url.toString());
      } catch (error: any) {
        setError(error.message);
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    }

    validateAccess();
  }, [currentUser, location.search, navigate]);

  // Redirect to sign in if not authenticated
  if (!currentUser && !loading) {
    return <Navigate to="/signin" />;
  }

  useEffect(() => {
    async function handleFormSubmission(event: MessageEvent) {
      if (event.data.type === 'form-submit' && currentUser) {
        try {
          const params = new URLSearchParams(location.search);
          const evaluatorId = params.get('evaluatorId');

          if (!evaluatorId) {
            throw new Error('Missing evaluator ID');
          }

          await updateEvaluatorContext(evaluatorId, currentUser.uid);
          toast.success('Context saved successfully');
          navigate('/dashboard');
        } catch (error: any) {
          console.error('Error saving context:', error);
          toast.error(error.message || 'Failed to save context');
        }
      }
    }

    window.addEventListener('message', handleFormSubmission);
    return () => window.removeEventListener('message', handleFormSubmission);
  }, [currentUser, location.search, navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out:', error);
      setError('Failed to log out');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-50"
    >
      <Header 
        onLogout={handleLogout}
        onProfileClick={() => navigate('/profile')}
        userData={userData}
      />
      
      <main className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
          
          {formUrl && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="w-full h-[calc(100vh-200px)] px-0 sm:px-0"
            >
              <iframe
                className="typeform-widget w-full h-full"
                src={formUrl}
                style={{ width: '100%', height: '100%', border: 'none', maxWidth: 'none' }}
                title="Provide Context"
                frameBorder="0"
                allow="camera; microphone; autoplay; encrypted-media; fullscreen"
              />
            </motion.div>
          )}
        </div>
      </main>
    </motion.div>
  );
}